import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { activeCuesAt, effectiveMatchTime } from '@core/timeline.ts';
import type { Settings } from '@core/settings/schema.ts';
import type { SubtitleStore, SubtitleSlot } from '../../state/subtitleStore.ts';
import type { VideoController } from '@ports/VideoController.ts';
import {
  isV2Ok,
  isV2QuotaError,
  v2Fetch,
  v2IdAuth,
} from '../../adapters/net/v2Client.ts';
import { createCard, removeCardById, findCardsByText, patchCardCache, type Deck, type Card } from '../../adapters/cards/cardsApi.ts';
import { useI18n } from '../../i18n/index.ts';
import { isCompactDim, useDeviceClass } from '../../hooks/useDeviceClass.ts';
import { resolveSlotLanguage } from './resolveSlotLanguage.ts';
import {
  migrateLearningTargetLanguage,
  toAzureTranslationCode,
} from '../../lib/azureLanguage.ts';
import { scheduleVisemes, visemeImageUrl, type VisemeFrame } from '../../lib/visemeTimeline.ts';
import type { StringKey } from '../../i18n/strings.ts';
import { SheetHandle } from '../../components/SheetHandle.tsx';
import starIcon from '../../../../extension/assets/image/icon/star.svg';
import starFillIcon from '../../../../extension/assets/image/icon/star-fill.svg';
import arrowsMoveIcon from '../../../../extension/assets/image/icon/arrows-move.svg';
import soundwaveIcon from '../../../../extension/assets/image/icon/soundwave.svg';

const SAMPLE_PROMPTS = [
  {
    id: '0',
    titleKey: 'learnPromptMeaning' as const satisfies StringKey,
    prompt: 'What is the meaning of "[selectedText]" in the sentence "[fullText]"? Explain it in short.',
  },
  {
    id: '1',
    titleKey: 'learnPromptExamples' as const satisfies StringKey,
    prompt: `I found "[selectedText]" in the sentence "[fullText]". I would like to learn about how "[selectedText]" is used. Can you provide five different examples of sentences using "[selectedText]"?

To answer my question, you must follow these rules:
1. Since I am learning [textLang], the examples should be in [textLang].
2. Also, provide a translation in [userLang] for each example, so that I can understand their meanings.
3. Only provide the examples; do not add any extra text or prompts.

[model=2]`,
  },
  {
    id: '2',
    titleKey: 'learnPromptDictionary' as const satisfies StringKey,
    prompt: `I am a language learner currently studying [textLang]. I found [selectedText] in [fullText] and I would like to understand the meaning of [selectedText].

You must provide four pieces of information:
1. Part of speech.
2. Pronunciation, if it's English then provide phonetics, if it's Japanese then provide Hiragana/Katakana/Romaji, and so on.
3. Meaning, explain the meaning of [selectedText] in the sentence [fullText] in a simplified manner.
4. Example sentence, provide another usage of [selectedText] with a translation in [userLang].

Additionally, you must follow this format to respond to my query:
1. Use only seven lines to return my results:
Part of speech
Pronunciation
Meaning
--
[textLang] example sentence
Translation of the example sentence in [userLang]
--
2. Only return the results, do not add any extra textual prompts.
3. Be as concise as possible, similar to the content in a dictionary.

Although I am communicating with you in English, you should respond in [userLang] so that I can share it with friends who speak [userLang].`,
  },
] as const;

const TTS_RATES = [
  { value: 'x-slow', labelKey: 'learnTtsRateXslow' as const satisfies StringKey },
  { value: 'slow', labelKey: 'learnTtsRateSlow' as const satisfies StringKey },
  { value: 'medium', labelKey: 'learnTtsRateMedium' as const satisfies StringKey },
] as const;

function clampPopupPosition(
  left: number,
  top: number,
  width: number,
  height: number,
): { left: number; top: number } {
  const vv = window.visualViewport;
  const vw = vv?.width ?? window.innerWidth;
  const vh = vv?.height ?? window.innerHeight;
  const ox = vv?.offsetLeft ?? 0;
  const oy = vv?.offsetTop ?? 0;
  const maxLeft = Math.max(ox, ox + vw - width);
  const maxTop = Math.max(oy, oy + vh - height);
  return {
    left: Math.min(maxLeft, Math.max(ox, left)),
    top: Math.min(maxTop, Math.max(oy, top)),
  };
}

function computePopupMaxHeight(subtitleTop: number): number {
  const vv = window.visualViewport;
  const vh = vv?.height ?? window.innerHeight;
  const oy = vv?.offsetTop ?? 0;
  if (vh <= 0) return 240;
  const viewportCap = Math.round(vh * 0.72);
  const spaceAbove = Math.max(0, subtitleTop - oy - 8);
  const floor = Math.min(200, viewportCap);
  if (spaceAbove >= floor) {
    return Math.min(Math.round(spaceAbove * 0.95), viewportCap);
  }
  return Math.max(floor, Math.min(viewportCap, Math.round(vh * 0.48)));
}

function stripCueText(raw: string | undefined): string {
  return (raw ?? '').replace(/<br[^>]*>/gi, '\n').replace(/&lrm;/gi, '');
}

function getRefTexts(
  store: SubtitleStore,
  slot: SubtitleSlot,
  video: VideoController | null,
): { ref: string; refTrans: string } {
  const mainState = store.getSlot('main');
  const secondState = store.getSlot('second');
  const nowMs = video?.getCurrentTimeMs() ?? 0;
  const mainT = effectiveMatchTime(nowMs, mainState.view.delayMs);
  const secondT = effectiveMatchTime(nowMs, secondState.view.delayMs);
  const mainCue =
    mainState.view.priority?.[0]
    ?? activeCuesAt(mainState.cues ?? [], mainT)[0];
  const secondCue =
    secondState.view.priority?.[0]
    ?? activeCuesAt(secondState.cues ?? [], secondT)[0];
  const mainText = stripCueText(mainCue?.text);
  const secondText = stripCueText(secondCue?.text);
  const mainTranslation = stripCueText(mainCue?.translation);
  const secondTranslation = stripCueText(secondCue?.translation);

  const isMain = slot === 'main';
  const ref = isMain ? mainTranslation || mainText : secondTranslation || secondText;
  const refTrans = isMain ? secondTranslation || secondText : mainTranslation || mainText;
  return { ref, refTrans };
}

export type LearningPopupProps = {
  selectedText: string;
  slot: SubtitleSlot;
  clientX: number;
  clientY: number;
  settings: Settings;
  store: SubtitleStore;
  video: VideoController | null;
  unlock: boolean;
  userId: string;
  userToken: string;
  decks: Deck[];
  deckId: string;
  onDeckChange: (deckId: string) => void;
  onClose: () => void;
};

export function LearningPopup({
  selectedText,
  slot,
  clientX,
  clientY,
  settings,
  store,
  video,
  unlock,
  userId,
  userToken,
  decks,
  deckId,
  onDeckChange,
  onClose,
}: LearningPopupProps) {
  const { t } = useI18n();
  const { dim, orientation } = useDeviceClass();
  // Compact: portrait → bottom sheet, landscape → side rail. Desktop → floating.
  const layoutMode: 'sheet' | 'rail' | 'floating' = !isCompactDim(dim)
    ? 'floating'
    : orientation === 'landscape'
      ? 'rail'
      : 'sheet';
  const docked = layoutMode !== 'floating';
  const popupRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originLeft: number;
    originTop: number;
  } | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [translation, setTranslation] = useState(() => t('learnLoading'));
  const [gptText, setGptText] = useState(() => t('learnLoadingAi'));
  const [showPrompts, setShowPrompts] = useState(false);
  const [gptCache, setGptCache] = useState<Record<string, string>>({});
  const [refTexts, setRefTexts] = useState({ ref: '', refTrans: '' });
  const [bookmarkStatus, setBookmarkStatus] = useState<string | null>(
    () => (unlock && userToken ? t('learnLoading') : null),
  );
  const [bookmarkReady, setBookmarkReady] = useState(!(unlock && userToken));
  const [guestBlocked, setGuestBlocked] = useState(false);
  const [ttsError, setTtsError] = useState<string | null>(null);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsRate, setTtsRate] = useState<'x-slow' | 'slow' | 'medium'>('medium');
  /** Extension: rate control appears only after a TTS response that includes visemes. */
  const [showTtsRate, setShowTtsRate] = useState(false);
  const [visemeId, setVisemeId] = useState<number | null>(null);
  const visemeCancelRef = useRef<(() => void) | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [savedCardId, setSavedCardId] = useState<string | null>(null);
  const savedCardIdRef = useRef<string | null>(null);
  savedCardIdRef.current = savedCardId;
  const [priorCards, setPriorCards] = useState<Card[]>([]);
  const [saving, setSaving] = useState(false);
  const wasPausedRef = useRef(false);
  /** Extension learning.isLoadingTranslate / isLoadingGPT — block overlapping asks. */
  const loadingTranslateRef = useRef(false);
  const loadingGptRef = useRef(false);
  /** Bumps on each open/selection so stale responses never write. */
  const queryGenRef = useRef(0);
  /** Cue context frozen at popup open (extension createUI snapshot). */
  const cueSnapshotRef = useRef({ ref: '', refTrans: '' });
  const storeRef = useRef(store);
  storeRef.current = store;
  const videoRef = useRef(video);
  videoRef.current = video;

  const stopVisemes = () => {
    const cancel = visemeCancelRef.current;
    visemeCancelRef.current = null;
    if (cancel) cancel();
    setVisemeId(null);
  };

  const stopAudio = () => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
  };

  // Slot / subtitle language for TTS + GPT baseLang (Azure-normalized).
  const slotLangRaw = resolveSlotLanguage(store, slot) ?? '';
  const code = toAzureTranslationCode(slotLangRaw) || slotLangRaw || 'en';
  // Azure GPT catalog key only — empty means user has not set a learning target (no silent default).
  const targetLang = migrateLearningTargetLanguage(settings.learningModeTargetLanguage);
  const canBookmark = Boolean(unlock && userToken);
  const showDeck = !guestBlocked && !savedCardId && (canBookmark ? bookmarkReady : true);
  const showStar = !guestBlocked && !savedCardId && (canBookmark ? bookmarkReady : true);
  const showStarFill = !guestBlocked && Boolean(savedCardId);

  useEffect(() => {
    if (!video) return;
    wasPausedRef.current = video.isPaused();
    video.pause();
    return () => {
      if (!wasPausedRef.current && !document.querySelector('.learning-popup')) {
        video.play();
      }
    };
  }, [video]);

  useEffect(() => {
    return () => {
      stopVisemes();
      stopAudio();
    };
  }, []);

  useLayoutEffect(() => {
    if (docked) {
      setPos(null);
      return;
    }
    const el = popupRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const half = rect.width / 2;
    const placed = clampPopupPosition(clientX - half, clientY - rect.height - 12, rect.width, rect.height);
    setPos(placed);
  }, [clientX, clientY, docked]);

  const loadGpt = useCallback(
    async (promptId: string, prompt: string) => {
      // Extension queryGPT: mutex while in flight.
      if (loadingGptRef.current) return;
      if (!targetLang) {
        setGptText('');
        setShowPrompts(false);
        return;
      }
      const gen = queryGenRef.current;
      loadingGptRef.current = true;
      setShowPrompts(false);
      setGptText(t('learnLoadingAi'));
      const refs = cueSnapshotRef.current;
      try {
        const json = await v2Fetch<{ content: string }>('POST', '/v2/gpt/ask', {
          body: {
            ...v2IdAuth(userId, userToken),
            text: selectedText,
            ref: refs.ref,
            code: targetLang,
            more: undefined,
            examples: undefined,
            baseLang: code || undefined,
            prompt,
            promptId,
          },
          token: userToken,
        });
        if (gen !== queryGenRef.current) return;
        if (isV2QuotaError(json)) {
          setGptText(t('learnQuota'));
          setShowPrompts(false);
          return;
        }
        if (!isV2Ok(json)) {
          setGptText(t('translationFailed'));
          setShowPrompts(false);
          return;
        }
        const content = json.data.content;
        setGptText(content);
        setShowPrompts(true);
        setGptCache((prev) => ({ ...prev, [promptId]: content }));
        // Extension: persist GPT answer onto an existing card when present.
        const cardId = savedCardIdRef.current;
        if (unlock && userId && userToken && cardId) {
          void patchCardCache(userId, userToken, cardId, promptId, content).catch((err) => {
            console.log(err);
          });
        }
      } finally {
        if (gen === queryGenRef.current) loadingGptRef.current = false;
      }
    },
    [selectedText, targetLang, userId, userToken, code, t, unlock],
  );

  // Extension initBookmark: look up prior cards for this text in the selected deck.
  useEffect(() => {
    if (!canBookmark) {
      setPriorCards([]);
      setBookmarkReady(true);
      setBookmarkStatus(null);
      return undefined;
    }
    let cancelled = false;
    setBookmarkReady(false);
    setBookmarkStatus(t('learnLoading'));
    setSavedCardId(null);
    void (async () => {
      try {
        const cards = await findCardsByText(userId, userToken, selectedText, deckId);
        if (cancelled) return;
        setPriorCards(cards);
        setBookmarkStatus(null);
        setBookmarkReady(true);
      } catch (err) {
        console.log(err);
        if (cancelled) return;
        setPriorCards([]);
        setBookmarkStatus(t('translationFailed'));
        setBookmarkReady(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canBookmark, userId, userToken, selectedText, deckId, t]);

  // Extension createUI: one translate + one default GPT per open.
  // Generation guard drops stale completions (StrictMode / selection change).
  useEffect(() => {
    const gen = ++queryGenRef.current;
    loadingTranslateRef.current = false;
    loadingGptRef.current = false;

    const snap = getRefTexts(storeRef.current, slot, videoRef.current);
    cueSnapshotRef.current = snap;
    setRefTexts(snap);
    setGptCache({});
    setShowPrompts(false);
    setTranslation(t('learnLoading'));
    setGptText(t('learnLoadingAi'));

    const defaultPrompt = SAMPLE_PROMPTS.find(
      (p) => p.id === (settings.learningModeDefaultPromptId || '2'),
    ) ?? SAMPLE_PROMPTS[2];

    void (async () => {
      if (!targetLang) {
        if (gen === queryGenRef.current) setTranslation(t('learnTargetNotSet'));
        return;
      }
      loadingTranslateRef.current = true;
      try {
        const json = await v2Fetch<{ content: string }>('POST', '/v2/gpt/translate', {
          body: {
            ...v2IdAuth(userId, userToken),
            text: selectedText,
            ref: snap.ref,
            code: targetLang,
            refTrans: true,
          },
          token: userToken,
        });
        if (gen !== queryGenRef.current) return;
        if (isV2QuotaError(json)) {
          setTranslation(t('learnQuota'));
          return;
        }
        if (!isV2Ok(json)) {
          setTranslation(t('translationFailed'));
          return;
        }
        const [wordTrans, refTrans] = json.data.content.split('<sep>');
        setTranslation(wordTrans);
        setRefTexts((prev) => ({ ...prev, refTrans: refTrans || prev.refTrans }));
        cueSnapshotRef.current = {
          ...cueSnapshotRef.current,
          refTrans: refTrans || cueSnapshotRef.current.refTrans,
        };
      } finally {
        if (gen === queryGenRef.current) loadingTranslateRef.current = false;
      }
    })();

    void (async () => {
      if (!targetLang) {
        if (gen === queryGenRef.current) {
          setGptText('');
          setShowPrompts(false);
        }
        return;
      }
      loadingGptRef.current = true;
      try {
        const json = await v2Fetch<{ content: string }>('POST', '/v2/gpt/ask', {
          body: {
            ...v2IdAuth(userId, userToken),
            text: selectedText,
            ref: snap.ref,
            code: targetLang,
            more: undefined,
            examples: undefined,
            baseLang: code || undefined,
            prompt: defaultPrompt.prompt,
            promptId: defaultPrompt.id,
          },
          token: userToken,
        });
        if (gen !== queryGenRef.current) return;
        if (isV2QuotaError(json)) {
          setGptText(t('learnQuota'));
          setShowPrompts(false);
          return;
        }
        if (!isV2Ok(json)) {
          setGptText(t('translationFailed'));
          setShowPrompts(false);
          return;
        }
        const content = json.data.content;
        setGptText(content);
        setShowPrompts(true);
        setGptCache((prev) => ({ ...prev, [defaultPrompt.id]: content }));
      } finally {
        if (gen === queryGenRef.current) loadingGptRef.current = false;
      }
    })();
  }, [
    selectedText,
    slot,
    targetLang,
    userId,
    userToken,
    code,
    settings.learningModeDefaultPromptId,
    t,
  ]);

  const playTts = async () => {
    if (ttsLoading) return;
    setTtsLoading(true);
    setTtsError(null);
    stopVisemes();
    stopAudio();
    try {
      const json = await v2Fetch<{ downloadURL: string; visemes?: VisemeFrame[] }>('POST', '/v2/tts', {
        body: {
          text: selectedText,
          code,
          ...v2IdAuth(userId, userToken),
          rate: ttsRate,
        },
        token: userToken,
      });
      if (isV2QuotaError(json)) {
        setTtsError(t('learnQuota'));
        return;
      }
      if (!isV2Ok(json)) {
        setTtsError(t('learnTtsFailed'));
        return;
      }
      const frames = Array.isArray(json.data.visemes) ? json.data.visemes : [];
      if (frames.length > 0) setShowTtsRate(true);
      const audio = new Audio(json.data.downloadURL);
      audioRef.current = audio;
      audio.addEventListener('ended', () => {
        stopVisemes();
      });
      const startVisemesAndPlay = () => {
        if (frames.length > 0) {
          visemeCancelRef.current = scheduleVisemes(frames, setVisemeId);
        }
        void audio.play().catch(() => {
          setTtsError(t('learnTtsFailed'));
          stopVisemes();
        });
      };
      if (frames.length > 0) {
        audio.addEventListener('canplaythrough', startVisemesAndPlay, { once: true });
        audio.load();
      } else {
        await audio.play();
      }
    } catch (err) {
      console.log(err);
      setTtsError(t('learnTtsFailed'));
      stopVisemes();
    } finally {
      setTtsLoading(false);
    }
  };

  const saveCard = async () => {
    if (!canBookmark) {
      setGuestBlocked(true);
      setBookmarkStatus(t('learnMembersSignIn'));
      return;
    }
    if (saving) return;
    setSaving(true);
    setBookmarkStatus(t('learnSaving'));
    setBookmarkReady(false);
    const refs = getRefTexts(store, slot, video);
    const deckName =
      decks.find((d) => d.id === deckId)?.name ??
      (deckId === 'uncategorized' ? t('cardsUncategorized') : deckId);
    try {
      const result = await createCard(userId, userToken, {
        deck: deckId,
        deckName,
        front: selectedText,
        frontLang: code,
        frontTrans: translation.startsWith(t('learnLoading')) ? '' : translation,
        transLang: targetLang,
        refSent: refs.ref,
        refTrans: refTexts.refTrans || refs.refTrans,
        nbsCache: gptCache,
      });
      if (!result || !isV2Ok(result)) {
        setBookmarkStatus(isV2QuotaError(result) ? t('learnQuota') : t('learnSaveFailed'));
        setBookmarkReady(true);
        return;
      }
      const savedId =
        (result as { id?: string }).id ??
        (isV2Ok(result) ? result.data?.id : undefined);
      setSavedCardId(typeof savedId === 'string' ? savedId : null);
      setBookmarkStatus(null);
    } catch (err) {
      console.log(err);
      setBookmarkStatus(t('learnSaveFailed'));
      setBookmarkReady(true);
    } finally {
      setSaving(false);
    }
  };

  const unsaveCard = async () => {
    if (!canBookmark || !savedCardId || saving) return;
    setSaving(true);
    setBookmarkStatus(t('learnSaving'));
    try {
      const result = await removeCardById(userId, userToken, savedCardId);
      if (!result || !isV2Ok(result)) {
        setBookmarkStatus(isV2QuotaError(result) ? t('learnQuota') : t('learnSaveFailed'));
        return;
      }
      setSavedCardId(null);
      setBookmarkStatus(null);
      setBookmarkReady(true);
    } catch (err) {
      console.log(err);
      setBookmarkStatus(t('learnSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const onDragStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || !pos) return;
    event.preventDefault();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originLeft: pos.left,
      originTop: pos.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.classList.add('is-dragging');
  };

  const onDragMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId || !popupRef.current) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const rect = popupRef.current.getBoundingClientRect();
    setPos(clampPopupPosition(drag.originLeft + dx, drag.originTop + dy, rect.width, rect.height));
  };

  const onDragEnd = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    event.currentTarget.classList.remove('is-dragging');
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  const maxHeight = layoutMode === 'floating' ? computePopupMaxHeight(clientY) : undefined;

  const panel = (
    <div
      ref={popupRef}
      className={`learning-popup${
        layoutMode === 'sheet'
          ? ' learning-popup--sheet'
          : layoutMode === 'rail'
            ? ' learning-popup--rail'
            : ''
      }`}
      data-testid="learning-popup"
      data-layout={layoutMode}
      style={
        layoutMode === 'floating'
          ? {
              position: 'fixed',
              left: pos?.left ?? clientX,
              top: pos?.top ?? clientY,
              maxHeight,
              zIndex: 2147483647,
            }
          : undefined
      }
      onMouseEnter={() => {
        if (layoutMode === 'floating') video?.pause();
      }}
      onMouseLeave={() => {
        if (layoutMode === 'floating' && !wasPausedRef.current) video?.play();
      }}
    >
      {layoutMode === 'sheet' ? (
        <SheetHandle
          onDismiss={onClose}
          className="learning-popup__handle"
          testId="learning-sheet-handle"
        />
      ) : null}

      {visemeId !== null ? (
        <div className="learning-popup__viseme" aria-hidden="true">
          <img data-testid="learning-viseme" src={visemeImageUrl(visemeId)} alt="" />
        </div>
      ) : null}

      <div className="learning-popup__topbar">
        <div className="learning-popup__topbar-left">
          {showStar ? (
            <button
              type="button"
              className="learning-popup__icon"
              data-testid="learning-save"
              aria-label={t('learnSave')}
              title={t('learnSave')}
              disabled={saving}
              onClick={() => void saveCard()}
            >
              <img src={starIcon} alt="" width={20} height={20} draggable={false} />
            </button>
          ) : null}
          {showStarFill ? (
            <button
              type="button"
              className="learning-popup__icon"
              data-testid="learning-unsave"
              aria-label={t('learnUnsave')}
              title={t('learnUnsave')}
              disabled={saving}
              onClick={() => void unsaveCard()}
            >
              <img src={starFillIcon} alt="" width={20} height={20} draggable={false} />
            </button>
          ) : null}
          {bookmarkStatus ? (
            <span className="learning-popup__status" data-testid="learning-bookmark-status">
              {bookmarkStatus}
            </span>
          ) : null}
          {showDeck ? (
            <select
              className="learning-popup__deck"
              value={deckId}
              onChange={(e) => onDeckChange(e.target.value)}
              aria-label={t('cardsDeck')}
            >
              <option value="uncategorized">{t('cardsUncategorized')}</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          ) : null}
        </div>
        <div className="learning-popup__topbar-right">
          {layoutMode === 'floating' ? (
            <button
              type="button"
              className="learning-popup__icon learning-popup__drag"
              data-testid="learning-move"
              aria-label={t('learnMove')}
              title={t('learnMove')}
              onPointerDown={onDragStart}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
            >
              <img src={arrowsMoveIcon} alt="" width={20} height={20} draggable={false} />
            </button>
          ) : null}
          <button
            type="button"
            className="learning-popup__close"
            data-testid="learning-close"
            aria-label={t('settingsClose')}
            title={t('settingsClose')}
            onClick={onClose}
          >
            ×
          </button>
        </div>
      </div>

      <div className="learning-popup__sound">
        {ttsLoading ? (
          <span className="learning-popup__loader" aria-hidden="true" />
        ) : (
          <button
            type="button"
            className="learning-popup__icon"
            data-testid="learning-speak"
            aria-label={t('learnSpeak')}
            title={t('learnSpeak')}
            onClick={() => void playTts()}
          >
            <img src={soundwaveIcon} alt="" width={20} height={20} draggable={false} />
          </button>
        )}
        {ttsError ? <span className="learning-popup__error">({ttsError})</span> : null}
        <span className="learning-popup__sound-text">{selectedText}</span>
      </div>

      {showTtsRate ? (
        <div className="learning-popup__rate" role="group" aria-label={t('learnTtsRate')}>
          {TTS_RATES.map((rate) => (
            <button
              key={rate.value}
              type="button"
              className={`learning-popup__rate-option${
                ttsRate === rate.value ? ' learning-popup__rate-option--active' : ''
              }`}
              data-testid={`learning-tts-rate-${rate.value}`}
              aria-pressed={ttsRate === rate.value}
              onClick={() => setTtsRate(rate.value)}
            >
              {t(rate.labelKey)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="learning-popup__translation" aria-live="polite">
        {translation}
      </div>

      <div className="learning-popup__gpt">
        <div className="learning-popup__gpt-text" aria-live="polite">
          {gptText}
        </div>
        {showPrompts ? (
          <div
            className="learning-popup__prompts"
            role="group"
            aria-label={t('settingsLearningDefaultPrompt')}
          >
            {SAMPLE_PROMPTS.map((p) => (
              <button
                key={p.id}
                type="button"
                className="learning-popup__prompt"
                title={t(p.titleKey)}
                onClick={() => {
                  void loadGpt(p.id, p.prompt);
                }}
              >
                {t(p.titleKey)}
              </button>
            ))}
          </div>
        ) : null}
        {priorCards.length > 0 ? (
          <div className="learning-popup__saved" data-testid="learning-prior-cards">
            {priorCards.map((card) => {
              const line = `${card.refSent ?? ''}${
                card.refTrans && card.refSent !== card.refTrans ? `\n${card.refTrans}` : ''
              }`;
              return (
                <p key={card.id} className="learning-popup__saved-item">
                  {line}
                </p>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return panel;

  return createPortal(
    <>
      {docked ? (
        <button
          type="button"
          className="learning-scrim"
          data-testid="learning-scrim"
          aria-label={t('settingsClose')}
          onClick={onClose}
        />
      ) : null}
      {panel}
    </>,
    document.body,
  );
}
